import React, { useState } from 'react';
import { X, ExternalLink, CheckCircle, Wrench } from 'lucide-react';
import { LinkedInDiagnostics } from './LinkedInDiagnostics';

interface LinkedInSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LinkedInSetupModal({ isOpen, onClose }: LinkedInSetupModalProps) {
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  if (!isOpen) return null;

  if (showDiagnostics) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">LinkedIn Diagnostics</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowDiagnostics(false)}
                className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Back to Setup Guide
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
          <div className="px-6 py-6">
            <LinkedInDiagnostics />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">LinkedIn Integration Setup</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              To connect LinkedIn, you need to create your own LinkedIn App and provide the Client ID and Client Secret.
              This gives you full control over your LinkedIn integration.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                1
              </span>
              Create LinkedIn App
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700">
                    Go to{' '}
                    <a
                      href="https://www.linkedin.com/developers/apps"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline inline-flex items-center"
                    >
                      LinkedIn Developers Portal
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Click "Create app" button</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 mb-2">Fill in the app details:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 text-sm text-gray-600">
                    <li>App name: Your app name (e.g., "My Social Media Manager")</li>
                    <li>LinkedIn Page: Select or create a LinkedIn company page</li>
                    <li>Privacy policy URL: Your website privacy policy</li>
                    <li>App logo: Upload a logo (optional but recommended)</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Agree to LinkedIn API Terms of Use and create the app</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                2
              </span>
              Configure OAuth Settings
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">In your app, go to the "Auth" tab</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 mb-2">Add OAuth 2.0 redirect URL:</p>
                  <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-sm text-gray-800 break-all">
                    https://sms.aiagenthub.pl/linkedin-callback
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Copy this exact URL to your LinkedIn App settings
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Click "Update" to save the redirect URL</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                3
              </span>
              Request Products (Scopes)
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Go to the "Products" tab in your app</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 mb-2">Request access to these products:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 text-sm text-gray-600">
                    <li><strong>Sign In with LinkedIn using OpenID Connect</strong> - For basic authentication (instant approval)</li>
                    <li><strong>Share on LinkedIn</strong> - To post on personal profiles (instant approval)</li>
                    <li><strong>Marketing Developer Platform</strong> - To post on company pages (requires review)</li>
                  </ul>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Marketing Developer Platform requires LinkedIn review (1-2 weeks).
                  You can start with personal profile posting while waiting for approval.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                4
              </span>
              Get Your Credentials
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">In the "Auth" tab, find your credentials:</p>
              </div>
              <div className="ml-7 space-y-2">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Client ID</p>
                  <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-sm text-gray-800">
                    Example: 77cxvfh4hvwpen
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Client Secret</p>
                  <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-sm text-gray-800">
                    Example: WPL_AP1.XXXXXXXXXXXXX.XXXXXX==
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Keep this secret secure and never share it publicly!</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                5
              </span>
              Add Credentials to Your App
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Go to "API Settings" in this application</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Paste your LinkedIn Client ID and Client Secret</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Click "Save" and then try connecting your LinkedIn account!</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">Ready to Connect!</h4>
            <p className="text-sm text-green-800">
              Once you've added your credentials in API Settings, you can connect your LinkedIn account
              and start posting to your profile or company pages.
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={() => setShowDiagnostics(true)}
              className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <Wrench className="w-4 h-4" />
              <span>Run Diagnostics</span>
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
